import React, { useState, useRef, useCallback, useEffect } from 'react';
import { SelfieSegmentation } from "@mediapipe/selfie_segmentation";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faImage, faUpload } from "@fortawesome/free-solid-svg-icons";
import { MediasfuColors, MediasfuBorders, MediasfuSpacing } from "../components_modern/core";
import { selfieSegmentationService } from "../components_modern/utils/SelfieSegmentationService";

export const defaultImages = [
  { thumb: "https://mediasfu.com/images/backgrounds/wall_thumbnail.jpg", src: "https://mediasfu.com/images/backgrounds/wall.jpg" },
  { thumb: "https://mediasfu.com/images/backgrounds/wall2_thumbnail.jpg", src: "https://mediasfu.com/images/backgrounds/wall2.jpg" },
  { thumb: "https://mediasfu.com/images/backgrounds/shelf_thumbnail.jpg", src: "https://mediasfu.com/images/backgrounds/shelf.jpg" },
  { thumb: "https://mediasfu.com/images/backgrounds/clock_thumbnail.jpg", src: "https://mediasfu.com/images/backgrounds/clock.jpg" },
  { thumb: "https://mediasfu.com/images/backgrounds/desert_thumbnail.jpg", src: "https://mediasfu.com/images/backgrounds/desert.jpg" },
  { thumb: "https://mediasfu.com/images/backgrounds/flower_thumbnail.jpg", src: "https://mediasfu.com/images/backgrounds/flower.jpg" },
];

interface VirtualBackgroundSelectorProps {
  virtualBackground: string | null;
  onSelect: (bg: string | null) => void;
  onUpload: (file: File) => void;
  isDarkMode: boolean;
  isLoading?: boolean;
}

export const VirtualBackgroundSelector: React.FC<VirtualBackgroundSelectorProps> = ({
  virtualBackground,
  onSelect,
  onUpload,
  isDarkMode,
  isLoading = false,
}) => {
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      onUpload(e.target.files[0]);
    }
  };

  return (
    <div style={{ marginTop: MediasfuSpacing.sm, position: "relative" }}>
      {isLoading && (
        <div style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: isDarkMode ? "rgba(15,23,42,0.85)" : "rgba(255,255,255,0.85)",
          borderRadius: MediasfuBorders.md,
          zIndex: 10,
          backdropFilter: "blur(2px)",
        }}>
          <div style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 8,
          }}>
            <div style={{
              width: 24,
              height: 24,
              border: `3px solid ${isDarkMode ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.1)"}`,
              borderTopColor: MediasfuColors.primary,
              borderRadius: "50%",
              animation: "spin 0.8s linear infinite",
            }} />
            <span style={{
              fontSize: "0.75rem",
              color: isDarkMode ? "#94a3b8" : "#64748b",
              fontWeight: 500,
            }}>Loading model...</span>
          </div>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      )}
      <label style={{ 
        display: "flex",
        alignItems: "center",
        gap: MediasfuSpacing.xs,
        fontSize: "0.8rem",
        fontWeight: 600,
        color: isDarkMode ? "#cbd5e1" : "#475569",
        marginBottom: 6,
      }}>
        <FontAwesomeIcon icon={faImage} style={{ marginRight: 6 }} />
        Virtual Background
      </label>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 6,
          marginTop: 6,
        }}
      >
        {/* None option */}
        <div
          onClick={() => onSelect(null)}
          style={{
            width: 48,
            height: 32,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            border: !virtualBackground || virtualBackground === "none"
              ? `2px solid ${MediasfuColors.primary}`
              : `1px solid ${isDarkMode ? "#475569" : "#cbd5e1"}`,
            borderRadius: MediasfuBorders.sm,
            cursor: "pointer",
            background: isDarkMode ? "#1e293b" : "#f1f5f9",
            fontSize: "0.65rem",
            fontWeight: 500,
            color: isDarkMode ? "#e2e8f0" : "#334155",
            transition: "all 0.2s ease",
          }}
          title="No background"
        >
          None
        </div>
        {/* Blur option */}
        <button
          type="button"
          aria-label="Blur background"
          onClick={() => onSelect("blur")}
          style={{
            width: 48,
            height: 32,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            border: virtualBackground === "blur"
              ? `2px solid ${MediasfuColors.primary}`
              : `1px solid ${isDarkMode ? "#475569" : "#cbd5e1"}`,
            borderRadius: MediasfuBorders.sm,
            cursor: "pointer",
            background: `linear-gradient(135deg, ${isDarkMode ? "#374151" : "#e2e8f0"}, ${isDarkMode ? "#1f2937" : "#cbd5e1"})`,
            fontSize: "0.65rem",
            fontWeight: 500,
            color: isDarkMode ? "#e2e8f0" : "#334155",
            backdropFilter: "blur(4px)",
            transition: "all 0.2s ease",
          }}
          title="Blur background"
        >
          Blur
        </button>
        {/* Background images */}
        {defaultImages.map((imgData, idx) => (
          <img
            key={idx}
            src={imgData.thumb}
            alt={`bg-${idx + 1}`}
            onClick={() => onSelect(imgData.src)}
            style={{
              width: 48,
              height: 32,
              objectFit: "cover",
              cursor: "pointer",
              borderRadius: MediasfuBorders.sm,
              border: virtualBackground === imgData.src
                ? `2px solid ${MediasfuColors.primary}`
                : `1px solid ${isDarkMode ? "#475569" : "#cbd5e1"}`,
              transition: "all 0.2s ease",
            }}
            title={`Background ${idx + 1}`}
          />
        ))}
      </div>
      <label
        htmlFor="bg-upload-expanded"
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          marginTop: MediasfuSpacing.md,
          cursor: "pointer",
          color: MediasfuColors.primary,
          fontSize: "0.75rem",
          fontWeight: 500,
        }}
      >
        <FontAwesomeIcon icon={faUpload} />
        Upload Custom
        <input
          type="file"
          id="bg-upload-expanded"
          accept="image/*"
          style={{ display: "none" }}
          onChange={handleFileChange}
        />
      </label>
    </div>
  );
};

export const useVirtualBackground = () => {
  const selfieSegmentation = useRef<SelfieSegmentation | null>(null);
  const [isModelLoading, setIsModelLoading] = useState(false);
  const pauseSegmentation = useRef(false);
  const processedStream = useRef<MediaStream | null>(null);

  const preloadModel = useCallback(async () => {
    // Use singleton service to get/create the model
    if (selfieSegmentation.current) return;
    
    setIsModelLoading(true);
    try {
      const model = await selfieSegmentationService.getModel();
      selfieSegmentation.current = model;
    } catch (error) {
      console.error("Failed to load selfie segmentation model", error);
    } finally {
      setIsModelLoading(false);
    }
  }, []);

  // On unmount, just clear local refs but DON'T destroy the singleton model
  // The model is shared and may be used by other components
  useEffect(() => {
    return () => {
      pauseSegmentation.current = true;
      processedStream.current?.getTracks().forEach((t) => t.stop());
      processedStream.current = null;
      // Clear local reference but don't close the singleton model
      selfieSegmentation.current = null;
    };
  }, []);

  return {
    selfieSegmentation,
    isModelLoading,
    preloadModel,
    pauseSegmentation,
    processedStream
  };
};
